<template>
  <wizard-form v-if="!hidden" v-bind="ouiaProps" :title="wizard.isFirstIteration ? $t('Riepilogo prodotti') : $t('Riepilogo prodotti da aggiungere a offerta')">
    <template #next-icon>
      <circle-check-icon />
    </template>

    <pf-alert variant="warning" v-if="!catalog.products.length" :title="$t('Nessun prodotto trovato')" class="pf-v6-u-mt-lg">
      <p>
        {{ $t("Con le opzioni da te selezionate non ci è al momento possibile generare un'offerta") }}
      </p>
      <p>
        <i18n-t keypath="Puoi provare a modificare le opzioni scelte oppure contattare il nostro supporto commerciale {0}." tag="span">
          <router-link :to="{ name: 'tickets' }">
            {{ $t('aprendo un ticket') }}
          </router-link>
        </i18n-t>
      </p>
    </pf-alert>

    <template v-else>
      <wizard-summary-section
        v-for="(products, dominio) of wizard.groupedProducts"
        :key="dominio"
        :group="dominio"
        :products="products"
        :currency="wizard.customer?.currency ?? 'EUR'"
        :free-period="wizard.freePeriod"
        :always-full-price="!wizard.fatturaMe"
        show-qt
        :ouia-id="`wizard-summary-section-${dominio}`"
      >
        <wizard-summary-hosting v-if="dominio === wizard.nomeDominio" />
        <wizard-summary-cloud v-if="dominio === wizard.serverCloudName" />
        <wizard-summary-firewall v-if="dominio === wizard.firewallName" />
      </wizard-summary-section>

      <pf-flex justify-content="space-between">
        <pf-flex-item>
          <wizard-question
            v-if="
              wizard.askDurataFatturazione ||
              (wizard.isFirstIteration && ((wizard.askClienti && wizard.customer) || (wizard.fatturaMe && !wizard.customer?.currency_locked)))
            "
            :title="$t('Opzioni di fatturazione')"
          >
            <pf-description-list horizontal class="payment-totals" term-width="30ch" style="max-width: 80ch">
              <template v-if="wizard.isFirstIteration">
                <pf-description-list-group v-if="wizard.askClienti && wizard.customer">
                  <pf-description-list-term> {{ $t('Offerta intestata a') }}: </pf-description-list-term>
                  <pf-description-list-description class="pf-v6-u-text-align-end">
                    <wizard-summary-option :value="wizard.customer.ragione_sociale" edit-step="WizardStepClienti" ouia-id="wizard-summary-fattura-a" />
                  </pf-description-list-description>
                </pf-description-list-group>

                <pf-description-list-group v-if="wizard.fatturaMe && wizard.askClienti">
                  <pf-description-list-term>{{ $t('Modalità') }}: </pf-description-list-term>
                  <pf-description-list-description class="pf-v6-u-text-align-end">
                    <wizard-summary-option
                      :value="wizard.notForResale ? $t('Not for resale') : $t('Rivendita')"
                      edit-step="WizardStepClienti"
                      ouia-id="wizard-summary-notforresale"
                    />
                  </pf-description-list-description>
                </pf-description-list-group>

                <pf-description-list-group v-if="wizard.fatturaMe && !wizard.customer?.currency_locked">
                  <pf-description-list-term>{{ $t('Valuta') }}: </pf-description-list-term>
                  <pf-description-list-description class="pf-v6-u-text-align-end">
                    <currency-toggle id="currency-dropdown" />
                  </pf-description-list-description>
                </pf-description-list-group>
              </template>

              <pf-description-list-group v-if="wizard.askDurataFatturazione">
                <pf-description-list-term>{{ $t('Ricorrenza') }}: </pf-description-list-term>
                <pf-description-list-description>
                  <wizard-question-durata v-model="wizard.fatturazioneAnnuale" />
                </pf-description-list-description>
              </pf-description-list-group>
            </pf-description-list>
          </wizard-question>
        </pf-flex-item>

        <pf-flex-item shrink>
          <div v-if="wizard.freePeriod">
            <strong class="free-label">{{ $t('I primi {0} giorni sono gratuiti', [30]) }}</strong>
          </div>

          <pf-description-list horizontal class="payment-totals" term-width="30ch" style="max-width: 80ch">
            <offer-total
              :totali="wizard.totaliWizard"
              :vat="wizard.customer === undefined || wizard.customer.vat > 0"
              durata="Annuale"
              :label="$t('Totale ricorrente annuale:')"
              big
              :currency="wizard.customer?.currency ?? 'EUR'"
            />
            <offer-total
              :totali="wizard.totaliWizard"
              :vat="wizard.customer === undefined || wizard.customer.vat > 0"
              durata="Mensile"
              :label="$t('Totale ricorrente mensile:')"
              big
              :currency="wizard.customer?.currency ?? 'EUR'"
            />
            <offer-total
              :totali="wizard.totaliWizard"
              :vat="wizard.customer === undefined || wizard.customer.vat > 0"
              durata="Una tantum"
              :label="$t('Totale una tantum:')"
              big
              :currency="wizard.customer?.currency ?? 'EUR'"
            />
          </pf-description-list>
        </pf-flex-item>
      </pf-flex>
    </template>
  </wizard-form>
</template>

<style scoped>
:deep(.pf-v6-c-description-list__text) {
  min-width: 0;
  justify-content: flex-end;
  display: flex;
}
</style>

<script lang="ts" setup>
import { useOUIAProps, type OUIAProps } from '@common/ouia';
import { useWizardStep } from '@/wizard';
import { useCatalogStore } from '@/store/catalog';

import WizardSummarySection from '../summary/wizard-summary-section.vue';
import WizardSummaryOption from '../summary/wizard-summary-option.vue';
import WizardSummaryHosting from '../summary/wizard-summary-hosting.vue';
import WizardSummaryCloud from '../summary/wizard-summary-cloud.vue';
import WizardSummaryFirewall from '../summary/wizard-summary-firewall.vue';
import WizardForm from '../wizard-form.vue';
import WizardQuestion from '../questions/wizard-question.vue';
import OfferTotal from '@/components/offer-total.vue';
import CurrencyToggle from '../../currency-toggle.vue';
import WizardQuestionDurata from '../questions/wizard-question-durata.vue';
import CircleCheckIcon from '@vue-patternfly/icons/circle-check-icon';

defineOptions({
  name: 'WizardStepAdd',
});

const props = defineProps<OUIAProps>();
const ouiaProps = useOUIAProps(props);

const { wizard, hidden, isEnabled } = useWizardStep({
  isEnabled: wizard => !wizard.modal,
});

const catalog = useCatalogStore();

defineExpose({
  isEnabled,
});
</script>
